import {
  Box,
  Divider,
  IconButton,
  Popover,
  Text,
  TextButton,
  Tooltip,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  Article,
  ChevronDown,
  ChevronUp,
  Settings,
  Statistics,
} from '@wix/wix-ui-icons-common';
import Long from 'long';
import { FunctionComponent, useEffect, useState } from 'react';

import { DATA_HOOKS } from '../../dataHooks';
import { useResponsive } from '../../hooks/useResponsive';
import { PageType } from '../../types';
import { isLong } from '../../utils/is-long';
import { useStore } from '../../zustand/stores';
import { PageData } from '../PageTemplate';

import css from './index.module.scss';

const BACKOFFICE_URL = 'https://wix-bo.com/kb-backoffice-fed/articles';
const LINK = { size: 'tiny', skin: 'light', underline: 'onHover' } as const;
const DATE = { day: 'numeric', month: 'short', year: 'numeric' } as const;

type AgentToolbarProps = {
  pageType: PageType;
  pageData: PageData;
};

const formatDate = (date?: number | Long) => {
  if (!date) {
    return '—';
  }
  const epoch = isLong(date) ? new Long(date.low, date.high).toNumber() : date;
  return new Date(epoch).toLocaleDateString('en-US', DATE);
};

const StatRow: FunctionComponent<{ label: string; date?: number | Long }> = ({
  label,
  date,
}) => (
  <Box gap="SP1">
    <Box width="100px">
      <Text size="tiny" secondary>
        {label}
      </Text>
    </Box>
    <Text size="tiny">{formatDate(date)}</Text>
  </Box>
);

export const AgentToolbar: FunctionComponent<AgentToolbarProps> = ({
  pageType,
  pageData,
}: AgentToolbarProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { isWixEmployee } = useStore((state) => state.userSlice);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isStatsOpen, setIsStatsOpen] = useState(false);

  const showAgentToolbar = isWixEmployee && !isMobile;
  const article = pageType === PageType.Article ? pageData.article : undefined;

  useEffect(() => {
    const { classList } = document.body;
    classList.toggle('agent-toolbar-open', showAgentToolbar && isExpanded);
    classList.toggle('agent-toolbar-close', showAgentToolbar && !isExpanded);
  }, [isExpanded, showAgentToolbar]);

  useEffect(() => setIsStatsOpen(false), [article?.id, isExpanded]);

  if (!showAgentToolbar) {
    return null;
  }

  return (
    <Box
      className={isExpanded ? css.bar : css.collapsedBar}
      dataHook={DATA_HOOKS.AGENT_TOOLBAR}
      align="space-between"
      verticalAlign="middle"
      paddingLeft="SP5"
      paddingRight="SP3"
    >
      {isExpanded && (
        <>
          <Text size="tiny" light>
            {t('public.user-toolbar.info.welcome-message')}
          </Text>
          <Box verticalAlign="middle" gap="SP4" height="24px">
            {article && (
              <>
                <Popover
                  shown={isStatsOpen}
                  onClickOutside={() => setIsStatsOpen(false)}
                  onEscPress={() => setIsStatsOpen(false)}
                  moveBy={{ x: 0, y: 10 }}
                  placement="bottom"
                  appendTo="window"
                  showArrow
                >
                  <Popover.Element>
                    <TextButton
                      {...LINK}
                      prefixIcon={<Statistics />}
                      onClick={() => setIsStatsOpen(!isStatsOpen)}
                    >
                      {t('public.user-toolbar.article.state.title')}
                    </TextButton>
                  </Popover.Element>
                  <Popover.Content>
                    <Box className={css.stats} direction="vertical" gap="SP1">
                      <StatRow
                        label={t('common.updated')}
                        date={article.lastPublishDate}
                      />
                      <StatRow
                        label={t('common.created')}
                        date={article.creationDate}
                      />
                    </Box>
                  </Popover.Content>
                </Popover>
                <Divider direction="vertical" skin="dark" />
                <TextButton
                  {...LINK}
                  as="a"
                  href={`${BACKOFFICE_URL}/${article.id}/edit`}
                  target="_blank"
                  rel="noopener"
                  prefixIcon={<Settings />}
                >
                  {t('public.user-toolbar.article.manage.title')}
                </TextButton>
              </>
            )}
          </Box>
          <Tooltip content={t('public.user-toolbar.nav-tooltip.articles')}>
            <IconButton
              as="a"
              href={BACKOFFICE_URL}
              target="_blank"
              rel="noopener"
              size="small"
              skin="light"
              priority="tertiary"
              ariaLabel={t('public.user-toolbar.nav-tooltip.articles')}
            >
              <Article />
            </IconButton>
          </Tooltip>
        </>
      )}
      <button
        type="button"
        className={css.toggle}
        aria-expanded={isExpanded}
        aria-label="Wix team member bar"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <ChevronUp size="18px" /> : <ChevronDown size="18px" />}
      </button>
    </Box>
  );
};
